export type FieldExtractor = (data: string | undefined) => string | Record<string, unknown> | null
export type YesNoExtractor = (
  data: string | undefined,
  score: number,
  threshold: number,
) => 'Yes' | 'No'

export interface MetricFieldConfig {
  actualLabel: string
  expectedLabel?: string
  extractActual: FieldExtractor | YesNoExtractor
  extractExpected?: FieldExtractor
  showExpected?: boolean
  useDescriptiveExpected?: boolean
  isYesNoExtractor?: boolean
}

export const extractMessage = (data: string | undefined): string | null => {
  if (!data) return null
  try {
    const parsed = JSON.parse(data)
    return parsed?.message ?? null
  } catch {
    return null
  }
}

const extractIntent = (data: string | undefined): string | null => {
  if (!data) return null
  try {
    const parsed = JSON.parse(data)
    return parsed?.intent ?? null
  } catch {
    return null
  }
}

const extractVariables = (data: string | undefined): Record<string, unknown> | null => {
  if (!data) return null
  try {
    const parsed = JSON.parse(data)
    return parsed?.variables ?? null
  } catch {
    return null
  }
}

const extractToolCall = (data: string | undefined): 'Yes' | 'No' => {
  if (!data) return 'No'
  try {
    const parsed = JSON.parse(data)
    const hasToolCall =
      parsed?.tool_name != null || parsed?.tools_called != null || parsed?.tool_call != null
    return hasToolCall ? 'Yes' : 'No'
  } catch {
    return 'No'
  }
}

const extractKBSearched = (data: string | undefined): 'Yes' | 'No' => {
  if (!data) return 'No'
  try {
    const parsed = JSON.parse(data)
    // Backend uses "asearch_knowledge_base" (non-sentinel) or "knowledge_base_lookup" (sentinel)
    const kbToolNames = ['asearch_knowledge_base', 'knowledge_base_lookup', 'knowledge_base']
    const hasKBCall =
      (parsed?.tool_name && kbToolNames.includes(parsed.tool_name)) ||
      (Array.isArray(parsed?.tools_called) &&
        parsed.tools_called.some((tool: string) => kbToolNames.includes(tool))) ||
      parsed?.kb_searched != null
    return hasKBCall ? 'Yes' : 'No'
  } catch {
    return 'No'
  }
}

const extractLoopDetected = (
  _data: string | undefined,
  score: number,
  threshold: number,
): 'Yes' | 'No' => {
  return score < threshold ? 'Yes' : 'No'
}

const extractExpectedTool = (data: string | undefined): string | null => {
  if (!data) return null
  try {
    const parsed = JSON.parse(data)
    if (parsed?.expected_tool != null) return parsed.expected_tool
    if (parsed?.tool_name != null) return parsed.tool_name
    return null
  } catch {
    return null
  }
}

export const METRIC_FIELD_CONFIG: Record<string, MetricFieldConfig> = {
  'Rigid Response Accuracy': {
    actualLabel: 'Actual Output:',
    expectedLabel: 'Expected Output:',
    extractActual: extractMessage,
    extractExpected: extractMessage,
    showExpected: true,
  },
  'Semi Rigid Response Accuracy': {
    actualLabel: 'Actual Output:',
    extractActual: extractMessage,
    showExpected: true,
    useDescriptiveExpected: true,
  },
  'Freeflow Response Accuracy': {
    actualLabel: 'Actual Output:',
    extractActual: extractMessage,
    showExpected: true,
    useDescriptiveExpected: true,
  },
  'Intent Detection': {
    actualLabel: 'Actual Intent Detected:',
    expectedLabel: 'Expected Intent:',
    extractActual: extractIntent,
    extractExpected: extractIntent,
    showExpected: true,
  },
  'Intent Accuracy': {
    actualLabel: 'Actual Intent Detected:',
    expectedLabel: 'Expected Intent:',
    extractActual: extractIntent,
    extractExpected: extractIntent,
    showExpected: true,
  },
  'Variable Extraction': {
    actualLabel: 'Actual Output:',
    expectedLabel: 'Expected Output:',
    extractActual: extractVariables,
    extractExpected: extractVariables,
    showExpected: true,
  },
  'Variable Accuracy': {
    actualLabel: 'Actual Output:',
    expectedLabel: 'Expected Output:',
    extractActual: extractVariables,
    extractExpected: extractVariables,
    showExpected: true,
  },
  'Knowledge Base Correctness': {
    actualLabel: 'KB Searched:',
    extractActual: extractKBSearched,
    showExpected: true,
    useDescriptiveExpected: true,
  },
  'Loop Detection': {
    actualLabel: 'Loop Detected:',
    extractActual: extractLoopDetected,
    showExpected: true,
    useDescriptiveExpected: true,
    isYesNoExtractor: true,
  },
  Hallucination: {
    actualLabel: 'Actual Output:',
    extractActual: extractMessage,
    showExpected: true,
    useDescriptiveExpected: true,
  },
  'Tool Correctness': {
    actualLabel: 'Tool Call Executed:',
    expectedLabel: 'Expected Tool:',
    extractActual: extractToolCall,
    extractExpected: extractExpectedTool,
    showExpected: false,
  },
  'Conversation Node Message Accuracy': {
    actualLabel: 'Actual Output:',
    expectedLabel: 'Expected Output:',
    extractActual: extractMessage,
    extractExpected: extractMessage,
    showExpected: true,
  },
  Accuracy: {
    actualLabel: 'Actual Output:',
    expectedLabel: 'Expected Output:',
    extractActual: extractMessage,
    extractExpected: extractMessage,
    showExpected: true,
  },
}

export const getMetricFieldConfig = (metricName: string): MetricFieldConfig | null => {
  const cleanName = metricName.replace('[GEval]', '').trim()
  return METRIC_FIELD_CONFIG[cleanName] ?? null
}

export const isVariablesField = (value: unknown): value is Record<string, unknown> => {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

export const isYesNoValue = (value: unknown): value is 'Yes' | 'No' => {
  return value === 'Yes' || value === 'No'
}
